import { FC } from 'react';
import { useFormContext } from 'react-hook-form';

import { Grid, Typography } from '@mui/material';
import clsx from 'clsx';

import { NumberInputControlled } from 'components/_basic/Form/NumberInput';
import { FormSectionCard } from 'components/_basic/FormSectionCard';
import { FormSectionFilledCard } from 'components/_basic/FormSectionFilledCard';
import { Loader } from 'components/_basic/Loader/Loader';
import { TooltipComponent } from 'components/_basic/TooltipComponent';

import { concatWithDot } from 'helpers/string';

import { IQuestionsFormProps } from './types';
import { DAY } from '../DaysInputControl/constants';
import { DaysInputControl } from '../DaysInputControl/DaysInputControl';

import styles from './QuestionsForm.module.scss';

const INPUT_FIELD_WIDTH = 198;

export const QuestionsForm: FC<IQuestionsFormProps> = ({
  questionList,
  disabled,
  title,
  name,
  collapsible = false,
  width = INPUT_FIELD_WIDTH,
  isLoading,
  fieldWarnings,
}) => {
  const { control } = useFormContext();

  return (
    <FormSectionCard collapsible={collapsible} title={title} disabled={disabled}>
      <FormSectionFilledCard>
        {isLoading ? (
          <Loader />
        ) : (
          <Grid container display={'flex'} flexDirection={'column'} gap={1}>
            {questionList.map(
              ({ question, name: questionName, adornmentType, inputType = 'number', maxValue }, index) => {
                const fieldName = concatWithDot(name, questionName);
                const warningMessage = fieldWarnings?.[questionName];

                const numberInput = (
                  <NumberInputControlled
                    disabled={disabled}
                    control={control}
                    name={fieldName}
                    id={fieldName}
                    sx={{ width }}
                    adornmentType={adornmentType}
                    maxValue={maxValue}
                    status={warningMessage ? 'error' : undefined}
                    className={clsx(warningMessage && styles.warningInput)}
                  />
                );

                return (
                  <Grid item xs className={styles.gridRow} key={index}>
                    <Grid item>
                      <Typography variant="subtitle1" className={styles.question}>
                        {question}
                      </Typography>
                    </Grid>

                    <Grid item>
                      {inputType === DAY ? (
                        <DaysInputControl
                          control={control}
                          name={fieldName}
                          id={fieldName}
                          sx={{ width }}
                          disabled={disabled}
                        />
                      ) : warningMessage ? (
                        <TooltipComponent title={warningMessage} placement="top">
                          <div className={styles.warningInputWrapper}>{numberInput}</div>
                        </TooltipComponent>
                      ) : (
                        numberInput
                      )}
                    </Grid>
                  </Grid>
                );
              },
            )}
          </Grid>
        )}
      </FormSectionFilledCard>
    </FormSectionCard>
  );
};
